import React, { useState, useMemo, useCallback, memo } from 'react';
import { Search, Phone, MessageCircle, Clock, User, Bot } from 'lucide-react';
import { useChatContext } from '../hooks/useChatContext';
import { Conversation } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface ConversationListProps {
  onSelectConversation: (conversation: Conversation) => void;
}

const ConversationList: React.FC<ConversationListProps> = memo(({ onSelectConversation }) => {
  const { state } = useChatContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [channelFilter, setChannelFilter] = useState<string>('all');

  const filteredConversations = useMemo(() => {
    return state.conversations.filter(conversation => {
      const matchesSearch = !searchTerm || 
        conversation.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conversation.customer_phone.includes(searchTerm);
      
      const matchesStatus = statusFilter === 'all' || conversation.status === statusFilter;
      const matchesChannel = channelFilter === 'all' || conversation.channel === channelFilter;

      return matchesSearch && matchesStatus && matchesChannel;
    });
  }, [state.conversations, searchTerm, statusFilter, channelFilter]);

  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-gray-100 text-gray-800';
      case 'ai-handled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }, []);

  const getChannelIcon = useCallback((channel: string) => {
    switch (channel) {
      case 'whatsapp': return <MessageCircle className="w-4 h-4 text-green-500" />;
      case 'web': return <User className="w-4 h-4 text-blue-500" />;
      default: return <Phone className="w-4 h-4 text-gray-500" />;
    }
  }, []);

  const handleConversationClick = useCallback((conversation: Conversation) => {
    onSelectConversation(conversation);
  }, [onSelectConversation]);

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Conversations</h2>
        
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 p-2 border border-gray-300 rounded text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="ai-handled">AI Handled</option>
            <option value="resolved">Resolved</option>
          </select>
          
          <select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
            className="flex-1 p-2 border border-gray-300 rounded text-sm"
          >
            <option value="all">All Channels</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="web">Web</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
          </select>
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-2">No conversations yet</p>
            <p className="text-sm text-gray-400">Conversations will appear here when customers start chatting</p>
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => handleConversationClick(conversation)}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                state.activeConversation?.id === conversation.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getChannelIcon(conversation.channel)}
                  <h3 className="font-medium text-gray-900 truncate">
                    {conversation.customer_name || 'Unknown Customer'}
                  </h3>
                  {conversation.status === 'ai-handled' && (
                    <Bot className="w-4 h-4 text-blue-500" />
                  )}
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(conversation.status)}`}>
                  {conversation.status}
                </span>
              </div>
              
              <p className="text-sm text-gray-600 mb-2">{conversation.customer_phone}</p>
              
              {conversation.last_message && (
                <p className="text-sm text-gray-500 truncate mb-2">
                  {conversation.last_message.content}
                </p>
              )}
              
              <div className="flex items-center justify-between text-xs text-gray-400">
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDistanceToNow(new Date(conversation.updated_at), { addSuffix: true })}
                </div>
                {conversation.unread_count && conversation.unread_count > 0 && (
                  <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs">
                    {conversation.unread_count}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
});

export default ConversationList;
